import React, { useCallback, useEffect, useRef, useState } from 'react';
import './offersmain.css';
import offerBanner from './ofertasmillonarias.png';
import OffersCard from '../OffersCard/OffersCard';
import RequestSvc from '../../services/RequestSvc';

const API_BASE_URL = 'https://pulpoia-ops.com/backend/ads';
const ADS_API_BASE = 'https://pulpodevia.com/ads';
const ADS_TOTALS_TIMEOUT_MS = 45000;
const ADS_TOTALS_CONCURRENCY = 3;

function createTimedRequestSignal(parentSignal, timeoutMs) {
  const controller = new AbortController();
  let didTimeout = false;

  const abortFromParent = () => {
    controller.abort(parentSignal?.reason);
  };

  if (parentSignal?.aborted) {
    abortFromParent();
  } else if (parentSignal) {
    parentSignal.addEventListener('abort', abortFromParent, { once: true });
  }

  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs);

  return {
    signal: controller.signal,
    didTimeout: () => didTimeout,
    cleanup: () => {
      clearTimeout(timeoutId);
      parentSignal?.removeEventListener('abort', abortFromParent);
    }
  };
}

// Helper: extract page_id from a fanpage URL (prefers view_all_page_id)
function getPageIdFromFanpageUrl(url) {
  if (!url || typeof url !== 'string') return null;
  // Normalize HTML-encoded ampersands
  const s = url.replace(/&amp;/g, '&');
  try {
    const u = new URL(s);
    const id =
      u.searchParams.get('view_all_page_id') ||
      u.searchParams.get('page_id') ||
      u.searchParams.get('id');
    if (id) return id;
  } catch (_) {
    // Fallback regex for non-standard or partial URLs
    const m = s.match(/[?&](view_all_page_id|page_id|id)=([^&]+)/i);
    if (m) return decodeURIComponent(m[2]);
  }
  return null;
}

function getAdsErrorType(result) {
  if (!result || result.ok) return null;
  if (result.timedOut) return 'timeout';
  if (result.aborted) return 'aborted';
  if (typeof result.error === 'string' && result.error.startsWith('HTTP ')) return 'http';
  if (result.error) return 'network';
  return 'unavailable';
}

async function fetchTotalAds(pageId, signal) {
  if (signal?.aborted) {
    return { ok: false, pid: pageId, aborted: true };
  }

  const timedRequest = createTimedRequestSignal(signal, ADS_TOTALS_TIMEOUT_MS);

  try {
    const res = await fetch(`${ADS_API_BASE}?gettotalads=${encodeURIComponent(pageId)}&proxy=on`, {
      signal: timedRequest.signal,
      cache: 'no-store'
    });
    if (!res.ok) {
      return { ok: false, pid: pageId, error: `HTTP ${res.status}` };
    }
    const data = await res.json().catch(() => ({}));
    const total = Number(data?.totalads ?? data?.totalAds ?? 0);
    const n = Number.isFinite(total) ? total : 0;
    return { ok: true, pid: pageId, n };
  } catch (err) {
    if (signal?.aborted) {
      return { ok: false, pid: pageId, aborted: true };
    }
    if (timedRequest.didTimeout()) {
      return { ok: false, pid: pageId, error: 'timeout', timedOut: true };
    }
    return { ok: false, pid: pageId, error: err?.message || 'network-error' };
  } finally {
    timedRequest.cleanup();
  }
}

async function fetchTotalAdsWithConcurrency(pageIds, signal, concurrency) {
  const settled = new Array(pageIds.length);
  let nextIndex = 0;

  const workerCount = Math.min(concurrency, pageIds.length);

  async function runWorker() {
    while (!signal?.aborted) {
      const currentIndex = nextIndex;
      nextIndex += 1;

      if (currentIndex >= pageIds.length) {
        return;
      }

      try {
        settled[currentIndex] = {
          status: 'fulfilled',
          value: await fetchTotalAds(pageIds[currentIndex], signal)
        };
      } catch (error) {
        settled[currentIndex] = {
          status: 'rejected',
          reason: error
        };
      }
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return settled;
}

// Small animated dots indicator " . .. ... "
function DotDots({ interval = 400 }) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), interval);
    return () => clearInterval(id);
  }, [interval]);
  // Reserve width to avoid layout shift (3 chars)
  return <span style={{ display: 'inline-block', width: 18 }} aria-live="polite">{dots}</span>;
}

// Componente para seleccionar Nicho
const NichoFilter = ({ onNichoChange, selectedNicho }) => {
  return (
    <div className="filter">
      <label htmlFor="nicho-select" className="label">Selecciona un Nicho:</label>
      <select
        id="nicho-select"
        name="FK_NichoCode"
        value={selectedNicho}
        onChange={(e) => onNichoChange(e.target.value)}
        className="input"
      >
        <option value="DINERO">Dinero</option>
        <option value="RELACIONES">Relaciones</option>
        <option value="SALUD">Salud</option>
        <option value="GASTRONOMIA">Gastronomía</option>
        <option value="MANUALIDADES">Manualidades</option>
        <option value="BELLEZA">Belleza</option>
        <option value="FINANZAS">Finanzas</option>
        <option value="EMPRENDIMIENTO">Emprendimiento</option>
        <option value="ESPIRITUALIDAD">Espiritualidad</option>
        <option value="FÍSICOS">Físicos</option>
        <option value="FINANZAS">Finanzas</option>
        <option value="OTROS">Otros</option>
      </select>
    </div>
  );
};

// (OfferCard inline component removed; using reusable OffersCard component)


const OfferView = ({ isSidebarOpen, setSidebarOpen, setNewRow, selectedOfferSubitem }) => {
  const [selectedNicho, setSelectedNicho] = useState('SALUD');
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [isFeatureUsageLoading, setIsFeatureUsageLoading] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10); // 2 rows of 5 ads
  const [offersBatchToken, setOffersBatchToken] = useState(0);

  const handleNichoChange = useCallback((newNicho) => {
    setSelectedNicho(newNicho);
    setCurrentPage(1);
  }, []);

  // Cache page_id -> total ads to avoid duplicate network calls
  const countsCacheRef = useRef(new Map());
  const offersRef = useRef([]);
  // Track page_ids currently in-flight to show dots
  const [pendingPids, setPendingPids] = useState(new Set());
  // Track per-page_id badges to signal increases
  const [adsBadges, setAdsBadges] = useState(new Map());
  // Track errors per page_id for ADS API
  const [adsErrors, setAdsErrors] = useState(new Map());
  // Circuit breaker: block further ADS updates after first hard failure; alert once
  const adsUpdatesBlockedRef = useRef(false);
  const alertShownRef = useRef(false);

  // Fetch offers with pagination
  const fetchOffers = useCallback(async (nichoCode, page = 1, lim = 10) => {
    if (!nichoCode) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/getnicho?nichoCode=${nichoCode}&limit=${lim}&page=${page}`);
      const result = await response.json();
      if (response.status === 404 || result.code === 404) {
        setOffers([]);
        setOffersBatchToken((value) => value + 1);
        setTotalPages(1);
      } else if (!response.ok) {
        throw new Error(result.message || 'Network response was not ok');
      } else {
        setOffers(result.data || []);
        setOffersBatchToken((value) => value + 1);
        // Set pagination info
        setCurrentPage(result.info?.currentPage || 1);
        setTotalPages(result.info?.totalPages || 1);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch offers when nicho, page, or limit changes
  useEffect(() => {
    fetchOffers(selectedNicho, currentPage, limit);
  }, [selectedNicho, currentPage, limit, fetchOffers]);

  useEffect(() => {
    let isMounted = true;

    const loadFeatureUsage = async () => {
      setIsFeatureUsageLoading(true);
      try {
        const requestSvc = new RequestSvc();
        const response = await requestSvc.get('https://pulpoia-ops.com/backend/ads/user-features');
        if (!isMounted) return;
        if (response?.error) {
          console.warn('[OfferView] Feature usage response error', response?.message);
          setFeatureUsage(null);
          return;
        }
        setFeatureUsage(Array.isArray(response?.data) ? response.data : null);
      } catch (err) {
        if (!isMounted) return;
        console.warn('[OfferView] Feature usage fetch failed', err);
        setFeatureUsage(null);
      } finally {
        if (isMounted) setIsFeatureUsageLoading(false);
      }
    };

    loadFeatureUsage();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    offersRef.current = offers;
  }, [offers]);

  useEffect(() => {
    const currentOffers = offersRef.current;
    if (!currentOffers || currentOffers.length === 0) return;

    // If circuit is open due to a previous failure, skip updates
    if (adsUpdatesBlockedRef.current) return;

    const abort = new AbortController();

    // Collect page_ids and determine which ones need fetching vs can be filled from cache
    const seen = new Set();
    const toFetch = [];
    let needsPatchFromCache = false;

    for (const o of currentOffers) {
      const pid = getPageIdFromFanpageUrl(o?.URL_AdsFanpage);
      if (!pid) continue;

      if (countsCacheRef.current.has(pid)) {
        needsPatchFromCache = true;
      } else if (!seen.has(pid)) {
        seen.add(pid);
        toFetch.push(pid);
      }
    }

    if (needsPatchFromCache) {
      setAdsErrors((prev) => {
        const next = new Map(prev);
        let didChange = false;
        for (const pid of seen) {
          if (countsCacheRef.current.has(pid) && next.has(pid)) {
            next.delete(pid);
            didChange = true;
          }
        }
        return didChange ? next : prev;
      });

      setOffers((prev) => {
        let didChange = false;
        const newBadges = new Map();
        const next = prev.map((o) => {
          const pid = getPageIdFromFanpageUrl(o?.URL_AdsFanpage);
          if (pid && countsCacheRef.current.has(pid)) {
            const newN = Number(countsCacheRef.current.get(pid));
            const prevN = Number(o.NumberOfAds);
            let updated = o;
            if (!Number.isFinite(prevN) || prevN !== newN) {
              updated = { ...o, NumberOfAds: newN };
              didChange = true;
            }
            if (Number.isFinite(prevN) && Number.isFinite(newN) && newN > prevN) {
              const diff = newN - prevN;
              if (diff > 10) newBadges.set(pid, 'fire');
              else if (diff > 3 && diff < 10) newBadges.set(pid, 'up');
            }
            return updated;
          }
          return o;
        });

        if (newBadges.size) {
          setAdsBadges((prevBadges) => {
            const m = new Map(prevBadges);
            newBadges.forEach((v, k) => m.set(k, v));
            return m;
          });
        }

        return didChange ? next : prev;
      });
    }

    if (toFetch.length === 0) {
      return () => abort.abort();
    }

    setPendingPids((prev) => {
      const next = new Set(prev);
      toFetch.forEach((pid) => next.add(pid));
      return next;
    });

    (async () => {
      try {
        const firstPid = toFetch[0];
        const probe = await fetchTotalAds(firstPid, abort.signal);
        if (abort.signal.aborted || probe?.aborted) return;

        if (!probe || !probe.ok) {
          adsUpdatesBlockedRef.current = true;
          if (!alertShownRef.current) {
            alertShownRef.current = true;
            console.log('No se pueden actualizar el total de anuncios de las ofertas');
          }
          setAdsErrors((prev) => {
            const m = new Map(prev);
            const errorType = getAdsErrorType(probe) || 'unavailable';
            toFetch.forEach((pid) => m.set(pid, errorType));
            return m;
          });
          return;
        }

        countsCacheRef.current.set(probe.pid, probe.n);
        setAdsErrors((prev) => {
          if (!prev.has(probe.pid)) return prev;
          const next = new Map(prev);
          next.delete(probe.pid);
          return next;
        });

        const rest = toFetch.slice(1);
        if (rest.length) {
          const settled = await fetchTotalAdsWithConcurrency(
            rest,
            abort.signal,
            ADS_TOTALS_CONCURRENCY
          );

          if (abort.signal.aborted) return;

          const newErrors = new Map();
          const successfulPids = [];
          for (let i = 0; i < settled.length; i++) {
            const s = settled[i];
            const pid = rest[i];
            if (s.status === 'fulfilled' && s.value && s.value.ok) {
              countsCacheRef.current.set(s.value.pid, s.value.n);
              successfulPids.push(s.value.pid);
            } else {
              const result = s.status === 'fulfilled' ? s.value : null;
              newErrors.set(pid, getAdsErrorType(result) || 'unavailable');
            }
          }

          if (newErrors.size || successfulPids.length) {
            setAdsErrors((prev) => {
              const m = new Map(prev);
              successfulPids.forEach((pid) => m.delete(pid));
              newErrors.forEach((v, k) => m.set(k, v));
              return m;
            });
          }
        }

        setOffers((prev) => {
          let didChange = false;
          const newBadges = new Map();
          const next = prev.map((o) => {
            const pid = getPageIdFromFanpageUrl(o?.URL_AdsFanpage);
            if (pid && countsCacheRef.current.has(pid)) {
              const newN = Number(countsCacheRef.current.get(pid));
              const prevN = Number(o.NumberOfAds);
              let updated = o;
              if (!Number.isFinite(prevN) || prevN !== newN) {
                updated = { ...o, NumberOfAds: newN };
                didChange = true;
              }
              if (Number.isFinite(prevN) && Number.isFinite(newN) && newN > prevN) {
                const diff = newN - prevN;
                if (diff > 10) newBadges.set(pid, 'fire');
                else if (diff > 3 && diff < 10) newBadges.set(pid, 'up');
              }
              return updated;
            }
            return o;
          });

          if (newBadges.size) {
            setAdsBadges((prevBadges) => {
              const m = new Map(prevBadges);
              newBadges.forEach((v, k) => m.set(k, v));
              return m;
            });
          }

          return didChange ? next : prev;
        });
      } finally {
        if (!abort.signal.aborted) {
          setPendingPids((prev) => {
            const next = new Set(prev);
            toFetch.forEach((pid) => next.delete(pid));
            return next;
          });
        }
      }
    })().catch(() => {
      // Swallow any stray unhandled rejections
    });

    return () => abort.abort();
  }, [offersBatchToken]);

  const handleDelete = (id) => {
    // Eliminación local; aquí podría llamarse a un endpoint si existe
    if (!id) return;
    setOffers((prev) => prev.filter((o) => o.id !== id));
  };


  // Pagination controls
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    const pages = [];
    // Show up to 5 page numbers, with ellipsis if needed
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) {
      end = Math.min(5, totalPages);
    } else if (currentPage >= totalPages - 2) {
      start = Math.max(1, totalPages - 4);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return (
      <div className="pagination">
        <button
          className="paginationBtn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >{'|<'}</button>
        <button
          className="paginationBtn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >Anterior</button>
        {start > 1 && <span className="paginationEllipsis">...</span>}
        {pages.map((p) => (
          <button
            key={p}
            className={`paginationBtn${p === currentPage ? ' active' : ''}`}
            onClick={() => onPageChange(p)}
            disabled={p === currentPage}
          >{p}</button>
        ))}
        {end < totalPages && <span className="paginationEllipsis">...</span>}
        <button
          className="paginationBtn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >Siguiente</button>
        <button
          className="paginationBtn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >{'>|'}</button>
      </div>
    );
  };

  // Grid style: 2 rows of 5 ads
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '1.5rem',
    margin: '2rem 0',
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const ofeUsage = Array.isArray(featureUsage)
    ? featureUsage.find((entry) => typeof entry?.FK_FeaturePlanCode === 'string' && entry.FK_FeaturePlanCode.includes('-Ofe'))
    : null;
  const canShowPagination = !isFeatureUsageLoading && ofeUsage && Number(ofeUsage.TotalUsed) > 0;

  return (
    <div>
      <div className="offerBannerWrapper">
        <img src={offerBanner} alt="Ofertas Millonarias" className="offerBanner" />
      </div>
      <NichoFilter onNichoChange={handleNichoChange} selectedNicho={selectedNicho} />

      {isLoading && <p className="textCenter">Cargando...</p>}
      {error && <p className="errorText">Error: {error}</p>}

      <div className="grid" style={gridStyle}>
        {!isLoading && offers.map((offer) => (
          <OffersCard
            key={offer.id}
            offer={offer}
            onEdit={setEditingOffer}
            onDelete={handleDelete}
            pendingPids={pendingPids}
            adsBadges={adsBadges}
            adsErrors={adsErrors}
          />
        ))}
        {!isLoading && offers.length === 0 && (
          <p className="infoText">No se encontraron ofertas para este nicho.</p>
        )}
      </div>

      {/* Pagination controls */}
      {canShowPagination && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default OfferView;
